import { useMutation, useQuery } from "@apollo/react-hooks";
import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import track, { useTracking } from "react-tracking";

import { ReactComponent as JoinStudy } from "assets/join-study.svg";
import {
  AssessmentCard,
  BackButton,
  Button,
  StyledGrid
} from "components/simple";
import {
  Consent,
  GetOrganizationConsentFormInfo,
  GetOrganizationName,
  GetUserInExpiredOrgStatus,
  OptOut,
  TerminateUserConsentCadence,
  TransferToBhpOnExpiry
} from "graphql/user/user.gql";
import {
  fetchUser,
  getUser,
  hideLoader,
  showErrorNotification,
  showLoader
} from "state";
import { spacingDefaults } from "style/constants";
import { JoinHeader, Paragraph } from "./styled";
import routesConfig from "utils/routesConfig";

const url = routesConfig.consent.path;

const ORGANIZATION_CONFIG = {
  OBHW: {
    consentModalBodyCopy: `This platform features proprietary protocols and assessments owned by the Center for BrainHealth at UT Dallas. Your data is private; we won't share it outside the Center or use it in research. BrainHealth coaches may view your scores and progress to provide feedback and coaching.`,
    headerTitle: "Welcome to Optimal BrainHealth for Warfighters",
    buttonText: "I Agree and Continue"
  },
  xponetiq: {
    consentModalBodyCopy: `Welcome to xponetiq premium offered in collaboration with the University of Texas at Dallas Center for BrainHealth®. Discover the power you have to positively change and improve your brain. The first step is determining your BrainHealth® Index, a validated, holistic snapshot of your brain's current fitness level. We are excited to launch you on your brain health journey together.`,
    headerTitle: "A New Way to a Healthier Brain",
    buttonText: "I Agree and Continue"
  },
  default: {
    consentModalBodyCopy: `By consenting to participate in The BrainHealth Project, you are confirming that you are 18 years of age or older, that you understand the information provided in the consent form and have no further questions, and that you freely agree to participate in the study. If that is all true, click on the "I Consent to Participate" button to begin.`,
    headerTitle: "I Consent to Participate",
    buttonText: "I Consent to Participate"
  }
};

const getOrganizationContent = (
  organizationDisplayName: string | null,
  shouldHideConsentForm: boolean
) => {
  if (organizationDisplayName === "OBHW") return ORGANIZATION_CONFIG.OBHW;
  if (organizationDisplayName === "xponetiq")
    return ORGANIZATION_CONFIG.xponetiq;
  if (shouldHideConsentForm) {
    return {
      consentModalBodyCopy: `By using the BrainHealth App, you embark on a journey to explore strategies that enhance your understanding of brain health through our training, habits, and resources.`,
      headerTitle: "I Agree and Continue",
      buttonText: "I Agree and Continue"
    };
  }
  return ORGANIZATION_CONFIG.default;
};

const useOrganizationData = () => {
  const { data: orgData } = useQuery(GetOrganizationConsentFormInfo, {
    fetchPolicy: "no-cache"
  });

  const { data: orgDisplayData } = useQuery(GetOrganizationName, {
    fetchPolicy: "no-cache"
  });

  return {
    organizationDisplayName: orgDisplayData?.me?.organization?.displayName,
    shouldHideConsentForm: orgData?.me?.organization?.hideConsentForm,
    shouldHidePostConsentQuestions:
      orgData?.me?.organization?.hidePostConsentQuestions
  };
};

const Join: React.FC = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const tracking = useTracking();
  const [consent] = useMutation(Consent);
  const [optOut] = useMutation(OptOut);
  const [terminateUserConsentCadence] = useMutation(
    TerminateUserConsentCadence
  );

  const {
    organizationDisplayName,
    shouldHideConsentForm,
    shouldHidePostConsentQuestions
  } = useOrganizationData();

  const content = getOrganizationContent(
    organizationDisplayName,
    shouldHideConsentForm
  );

  const isOrgOBHW = organizationDisplayName === "OBHW";
  const isOrgXponetiq = organizationDisplayName === "xponetiq";

  const [transferToBhpOnExpiry] = useMutation(TransferToBhpOnExpiry);
  const [marketingOptIn, setMarketingOptIn] = useState(false);
  const user = useSelector(getUser);
  const [isUserInExpiredOrg, setIsUserInExpiredOrg] = useState(false);
  useQuery(GetUserInExpiredOrgStatus, {
    onCompleted: data => {
      setIsUserInExpiredOrg(data?.me?.isInExpiredOrg);
    }
  });

  async function updateConsent(consented: boolean): Promise<void> {
    dispatch(showLoader());
    tracking.trackEvent({
      url,
      actions: ["Consent", consented ? "Consent to Join" : "I do not consent"]
    });
    if (consented) {
      await consent({
        variables: { input: { marketingOptIn: marketingOptIn } }
      });
    } else if (user?.forcedConsent) {
      await terminateUserConsentCadence();
      dispatch(hideLoader());
      history.push("/consent");
      return;
    } else {
      await optOut();
      history.push("/consent/join");
    }
    dispatch(fetchUser());
    consented
      ? history.push("/notification_preferences")
      : history.push("/dashboard");
  }

  async function moveToBhp() {
    try {
      dispatch(showLoader());
      const response = await transferToBhpOnExpiry();
      return response;
    } catch (error) {
      dispatch(showErrorNotification("Uh oh, something went wrong"));
      dispatch(hideLoader());
    }
  }

  const [disabled, setDisabled] = useState(false);
  return (
    <>
      {!shouldHideConsentForm && !shouldHidePostConsentQuestions && (
        <BackButton location="/consent/form" />
      )}
      <StyledGrid container item justifyContent="center">
        <AssessmentCard>
          <StyledGrid item marginTop={spacingDefaults.large}>
            <JoinStudy />
          </StyledGrid>
          <StyledGrid item margin={spacingDefaults.normal}>
            <JoinHeader>{content.headerTitle}</JoinHeader>
          </StyledGrid>
          <StyledGrid
            item
            maxWidth="550px"
            mobileMaxWidth="80%"
            marginBottom={spacingDefaults.normal}
          >
            <Paragraph>{content.consentModalBodyCopy}</Paragraph>
          </StyledGrid>
          <StyledGrid item margin={spacingDefaults.normal}>
            <Button
              onClick={() => {
                if (isUserInExpiredOrg) {
                  moveToBhp().then(response => {
                    if (response) updateConsent(true);
                  });
                } else {
                  updateConsent(true);
                  setDisabled(true);
                }
              }}
            >
              {content.buttonText}
            </Button>
          </StyledGrid>
          {!shouldHideConsentForm && !isOrgOBHW && !isOrgXponetiq && (
            <StyledGrid item margin={spacingDefaults.normal}>
              <Button
                plain
                onClick={() => {
                  updateConsent(false);
                  setDisabled(true);
                }}
                disabled={disabled}
                style={{ textDecoration: "underline" }}
              >
                I do not consent
              </Button>
            </StyledGrid>
          )}
        </AssessmentCard>
      </StyledGrid>
    </>
  );
};

export default track({
  url
})(Join);
